// Utilities for applying client-side filters/search to conversations.
// This is intentionally UI-focused: it mirrors the filter payload generated by SharedFilterBar
// and keeps the sidebar consistent when socket events update the raw conversations list.

const toBool = (v) => {
  if (v === true || v === false) return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return v;
};

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const includesCaseInsensitive = (haystack, needle) => {
  const h = (haystack || '').toString().toLowerCase();
  const n = (needle || '').toString().trim().toLowerCase();
  if (!n) return true;
  return h.includes(n);
};

export const getConversationFilterValue = (conversation, key) => {
  switch (key) {
    case 'contact_type':
      return conversation?.contact?.contact_type;
    case 'is_ignored':
      return conversation?.contact?.is_ignored;
    case 'is_snoozed':
      return conversation?.contact?.is_snoozed;
    case 'is_virtual':
      return conversation?.contact?.is_virtual;
    case 'is_seen_by_user':
      return conversation?.is_seen_by_user;
    case 'status':
      return conversation?.status;
    case 'id':
      return conversation?.id;
    case 'agent_id':
      return conversation?.agent_id;
    case 'contact_id':
      return conversation?.contact_id;
    case 'updated_time':
      return conversation?.updated_time;
    case 'created_at':
      return conversation?.created_at;
    case 'updated_at':
      return conversation?.updated_at;
    default:
      return conversation?.[key];
  }
};

export const matchesConversationFilters = (conversation, normalizedFilters = {}) => {
  if (!normalizedFilters || typeof normalizedFilters !== 'object') return true;

  // Date range keys produced by SharedFilterBar are flattened as *_start / *_end
  const dateFields = ['updated_time', 'created_at', 'updated_at'];
  for (const fieldKey of dateFields) {
    const start = normalizedFilters[`${fieldKey}_start`];
    const end = normalizedFilters[`${fieldKey}_end`];
    if (!start && !end) continue;

    const convDate = safeDate(getConversationFilterValue(conversation, fieldKey));
    if (!convDate) return false;

    const startDate = safeDate(start);
    const endDate = safeDate(end);

    if (startDate && convDate < startDate) return false;
    if (endDate && convDate > endDate) return false;
  }

  // Non-date filters
  for (const [key, value] of Object.entries(normalizedFilters)) {
    if (key.endsWith('_start') || key.endsWith('_end')) continue;

    // SharedFilterBar may emit {type, op, value} for string/number filters.
    // Conversations sidebar currently does not use those, so ignore to avoid false negatives.
    if (value && typeof value === 'object' && !Array.isArray(value)) continue;

    const convValue = toBool(getConversationFilterValue(conversation, key));
    if (Array.isArray(value)) {
      const normalized = value.map(toBool);
      if (!normalized.includes(convValue)) return false;
    } else {
      if (toBool(value) !== convValue) return false;
    }
  }

  return true;
};

export const matchesConversationSearch = (conversation, searchText) => {
  const query = (searchText || '').trim();
  if (!query) return true;

  const name = `${conversation?.contact?.first_name || ''} ${conversation?.contact?.last_name || ''}`.trim();
  const lastContent = conversation?.last_message?.content || '';
  return includesCaseInsensitive(name, query) || includesCaseInsensitive(lastContent, query);
};

export const applyConversationClientSideFilters = ({
  conversations,
  searchText,
  normalizedFilters,
}) => {
  if (!Array.isArray(conversations)) return [];

  let list = conversations.filter(Boolean);

  if (normalizedFilters && Object.keys(normalizedFilters).length > 0) {
    list = list.filter((c) => matchesConversationFilters(c, normalizedFilters));
  }

  if ((searchText || '').trim()) {
    list = list.filter((c) => matchesConversationSearch(c, searchText));
  }

  return list;
};



